import * as monaco from 'monaco-editor';
import { TToken, TTokenAscii, TTokenComment, TTokenEscape } from '../../elf-core/ElfTokenizer';
import type { MonacoContext } from './App';

export function applyTokensAsDecorations(
	editor: monaco.editor.IStandaloneCodeEditor,
	ctx: MonacoContext,
	tokens: (TToken | TTokenComment | TTokenEscape)[],
	errors: { message: string; line: number; column: number; endLine: number; endColumn: number }[],
	warnings: { message: string; line: number; column: number; endLine: number; endColumn: number }[],
): void {
	const asciiTokens: TTokenAscii[] = [];
	const decorations: monaco.editor.IModelDeltaDecoration[] = tokens
		.filter((token) => token.type !== 'SPACE') // optional: skip spaces
		.map((token) => {
			let extendStyleColumns = 0;
			let additionalClass = '';

			switch (token.type) {
				case 'ASCII': {
					asciiTokens.push(token);
					break;
				}
				case 'ESCAPE': {
					extendStyleColumns = 1;
					break;
				}
				case 'KEYWORD': {
					additionalClass = ` my-token-keyword-extra-${token.token.toLowerCase()}`;
					break;
				}
				case 'LABEL': {
					switch (token.assumedType) {
						case 'FUNCTION':
						case 'CONSTANT':
							additionalClass = ` my-token-label-extra-${token.assumedType.toLowerCase()}`;
							break;
					}
					break;
				}
				case 'SYMBOL': {
					switch (token.token) {
						case '{':
						case '}': {
							additionalClass = ' my-token-symbol-extra-curlybrackets';
							break;
						}
						case '[':
						case ']': {
							additionalClass = ' my-token-symbol-extra-squarebrackets';
							break;
						}
					}
					break;
				}
			}

			return ({
				range: new monaco.Range(
					token.line,
					token.column,
					token.lastLine,
					token.lastColumn + 1 + extendStyleColumns,
				),
				options: {
					inlineClassName: `my-token-${token.type.toLowerCase()}${additionalClass}`,
				},
			});
		});

	const asciiDecorations = asciiTokens.map((token) => {
		return ({
			range: new monaco.Range(
				token.line,
				token.column,
				token.line,
				token.column + 1,
			),
			options: {
				inlineClassName: `my-token-prefix-${token.type.toLowerCase()}`,
			},
		});
	});

	const errorDecorations = errors.map((errorLine) => ({
		range: new monaco.Range(errorLine.line, 1, errorLine.line, 1), // single point is fine for lineClassName
		options: {
			isWholeLine: true,
			className: 'my-error-line', // background class
			glyphMarginClassName: 'my-error-glyph', // optional icon in the gutter
		},
	}));

	// dont add + 1 to end column as errorMapper already did that!
	const errorMarkers = errors.map((errorLine) => ({
		startLineNumber: errorLine.line,
		startColumn: errorLine.column,
		endLineNumber: errorLine.endLine,
		endColumn: errorLine.endColumn,
		message: errorLine.message,
		severity: monaco.MarkerSeverity.Error,
	}));
	const warningMarkers = warnings.map((errorLine) => ({
		startLineNumber: errorLine.line,
		startColumn: errorLine.column,
		endLineNumber: errorLine.endLine,
		endColumn: errorLine.endColumn,
		message: errorLine.message,
		severity: monaco.MarkerSeverity.Warning,
	}));
	const allMarkers = [...warningMarkers, ...errorMarkers];

	monaco.editor.setModelMarkers(editor.getModel()!, 'owner-id', allMarkers);

	// replace previous decorations with new ones
	ctx.currentDecorations = editor.deltaDecorations(ctx.currentDecorations, [...decorations, ...asciiDecorations, ...errorDecorations]);
}
